/**
 * CSV export helpers.
 *
 * Built client-side from data the page already has rather than as a download
 * endpoint, because the API is authenticated with a bearer header - a plain
 * <a href> download would not carry the token.
 */

// Wrap in quotes and double any inner quotes. The leading-quote guard stops
// spreadsheet software treating a leading =, +, - or @ as a formula.
function escapeCell(value) {
  if (value === null || value === undefined) {
    return '""';
  }

  let text = String(value);

  // Guard against a leading =, +, - or @ being run as a formula - but not at the
  // cost of turning a genuine negative number, such as a net loss, into text
  const isNumeric = /^-?\d+(\.\d+)?$/.test(text);

  if (/^[=+\-@]/.test(text) && !isNumeric) {
    text = `'${text}`;
  }

  return `"${text.replace(/"/g, '""')}"`;
}

export function toCsv(rows) {
  return rows.map((row) => row.map(escapeCell).join(',')).join('\r\n');
}

export function downloadCsv(filename, rows) {
  // The BOM makes Excel read it as UTF-8 rather than the system codepage
  const blob = new Blob(['﻿' + toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Turn a report payload into rows: a summary block, then the item breakdown.
 */
export function buildReportRows(report, periodLabel) {
  const money = (value) => (Number(value) || 0).toFixed(2);

  const rows = [
    ['Report', periodLabel],
    ['Generated', new Date().toLocaleString()],
    [],
    ['Summary', 'Amount (Rs.)'],
    ['Cash sales', money(report.cashRevenue)],
    ['Bank sales', money(report.bankRevenue)],
    ['Sold on due', money(report.dueRevenue)],
    ['Total revenue', money(report.totalRevenue)],
    ['Total discounts', money(report.totalDiscounts)],
    ['Total expenses', money(report.totalExpenses)],
    ['Net profit', money(report.netProfit)],
    ['Orders', report.totalOrders ?? 0],
    []
  ];

  if (report.itemsSold?.length) {
    rows.push(['Item', 'Quantity', 'Unit price', 'Discount', 'Revenue']);

    [...report.itemsSold]
      .sort((a, b) => b.quantity - a.quantity)
      .forEach((item) => {
        rows.push([
          item.itemName,
          item.quantity,
          money(item.price),
          money(item.totalDiscount),
          money(item.totalRevenue)
        ]);
      });
  }

  return rows;
}
